import * as React from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
  FloatingArrow,
} from '@floating-ui/react'
import type { Placement } from '@floating-ui/react'
import styled from 'styled-components'

export type TooltipProps = {
  initialOpen?: boolean
  placement?: Placement
  open?: boolean
  onOpenChange?: (open: boolean) => void
  arrowPlacement?: 'start' | 'center' | 'end'
}

function useTooltip({
  initialOpen = false,
  placement = 'top',
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  arrowPlacement = 'center',
}: TooltipProps = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen)

  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = setControlledOpen ?? setUncontrolledOpen

  const arrowRef = React.useRef(null)

  // Adjust the placement based on arrowPlacement
  const adjustedPlacement = React.useMemo(() => {
    const basePlacement = placement.split('-')[0] as
      | 'top'
      | 'bottom'
      | 'left'
      | 'right'
    if (arrowPlacement === 'center') {
      return basePlacement
    }
    if (arrowPlacement === 'start' || arrowPlacement === 'end') {
      // Start and end are actually counter-intuitive for so we flip them
      return `${basePlacement}-${arrowPlacement === 'start' ? 'end' : 'start'}` as Placement
    }
    return placement
  }, [placement, arrowPlacement])

  const data = useFloating({
    placement: adjustedPlacement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        crossAxis: adjustedPlacement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
      arrow({ element: arrowRef, padding: 5 }), // Include arrow middleware
    ],
  })

  const context = data.context

  const hover = useHover(context, {
    move: false,
    enabled: controlledOpen == null,
  })
  const focus = useFocus(context, {
    enabled: controlledOpen == null,
  })
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const interactions = useInteractions([hover, focus, dismiss, role])

  return React.useMemo(
    () => ({
      open,
      setOpen,
      arrowRef, // Include arrowRef
      ...interactions,
      ...data,
    }),
    [open, setOpen, arrowRef, interactions, data]
  )
}

type ContextType = ReturnType<typeof useTooltip> | null

const TooltipContext = React.createContext<ContextType>(null)

const useTooltipContext = () => {
  const context = React.useContext(TooltipContext)

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />')
  }

  return context
}

export function Tooltip({
  children,
  ...options
}: { children: React.ReactNode } & TooltipProps) {
  const tooltip = useTooltip(options)
  return (
    <TooltipContext.Provider value={tooltip} data-testid='tooltip-provider'>
      {children}
    </TooltipContext.Provider>
  )
}

export const TooltipTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & { asChild?: boolean }
>(function TooltipTrigger({ children, asChild = false, ...props }, propRef) {
  const context = useTooltipContext()
  const childrenRef = (children as any).ref
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef])

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...children.props,
        'data-state': context.open ? 'open' : 'closed',
      })
    )
  }

  return (
    <button
      ref={ref}
      // The user can style the trigger based on the state
      data-state={context.open ? 'open' : 'closed'}
      {...context.getReferenceProps(props)}
      data-testid='tooltip-trigger'
    >
      {children}
    </button>
  )
})

const TooltipEl = styled.div`
  background: var(--gray900);
  color: var(--white);
  padding: 10px;
  border-radius: var(--border-radius-sm);
  font-size: 14px;
`

const Arrow = styled(FloatingArrow)`
  fill: var(--gray900);
`

export const TooltipContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>(function TooltipContent({ style, ...props }, propRef) {
  const { context: floatingContext, arrowRef, ...context } = useTooltipContext()
  const ref = useMergeRefs([context.refs.setFloating, propRef])

  if (!context.open) return null

  return (
    <FloatingPortal>
      <TooltipEl
        ref={ref}
        style={{
          position: context.strategy,
          top: context.y ?? '',
          left: context.x ?? '',
          zIndex: 999999,
          maxWidth: '300px',
          ...style,
        }}
        {...context.getFloatingProps(props)}
        data-testid='tooltip-content'
      >
        <Arrow
          className='popper-arrow'
          ref={arrowRef}
          context={floatingContext}
          stroke='var(--gray200)'
          strokeWidth={1}
          data-testid='tooltip-arrow'
        />
        {props.children}
      </TooltipEl>
    </FloatingPortal>
  )
})

export function TooltipWrapper({
  children,
  tooltip,
  triggerProps,
  contentProps,
  ...rest
}: {
  children: React.ReactNode
  tooltip?: React.ReactNode
  props?: TooltipProps
  triggerProps?: React.ComponentProps<typeof TooltipTrigger>
  contentProps?: React.ComponentProps<typeof TooltipContent>
} & React.ComponentProps<typeof Tooltip>) {
  return (
    <Tooltip {...rest}>
      <TooltipTrigger asChild {...triggerProps}>{children}</TooltipTrigger>
      <TooltipContent {...contentProps}>{tooltip}</TooltipContent>
    </Tooltip>
  )
}
